import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import "./style.css";

const TAP_MOVE_THRESHOLD = 12;

function SelectorGridButton({
  className,
  selected,
  label,
  emoji,
  emojiClassName,
  labelClassName,
  onSelect,
}) {
  const startRef = useRef(null);

  return (
    <button
      type="button"
      className={className}
      aria-pressed={selected}
      title={label}
      onPointerDown={(e) => {
        startRef.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
      }}
      onPointerUp={(e) => {
        if (!startRef.current || startRef.current.id !== e.pointerId) return;
        const dx = Math.abs(e.clientX - startRef.current.x);
        const dy = Math.abs(e.clientY - startRef.current.y);
        startRef.current = null;
        if (dx <= TAP_MOVE_THRESHOLD && dy <= TAP_MOVE_THRESHOLD) {
          e.preventDefault();
          e.stopPropagation();
          onSelect();
        }
      }}
      onPointerCancel={() => {
        startRef.current = null;
      }}
    >
      <span className={`${emojiClassName} notranslate`} translate="no" aria-hidden="true">
        {emoji}
      </span>
      <span className={labelClassName}>{label}</span>
    </button>
  );
}

const feelings = [
  { id: "happy", emoji: "😊", label: "Happy" },
  { id: "sad", emoji: "😢", label: "Sad" },
  { id: "angry", emoji: "😠", label: "Angry" },
  { id: "excited", emoji: "🤩", label: "Excited" },
  { id: "grateful", emoji: "🙏", label: "Grateful" },
  { id: "blessed", emoji: "🙌", label: "Blessed" },
  { id: "loved", emoji: "💕", label: "Loved" },
  { id: "thankful", emoji: "🙏", label: "Thankful" },
  { id: "amazed", emoji: "😲", label: "Amazed" },
  { id: "proud", emoji: "😎", label: "Proud" },
  { id: "hopeful", emoji: "✨", label: "Hopeful" },
  { id: "confident", emoji: "💪", label: "Confident" },
  { id: "peaceful", emoji: "😌", label: "Peaceful" },
  { id: "joyful", emoji: "😄", label: "Joyful" },
  { id: "inspired", emoji: "💡", label: "Inspired" },
  { id: "motivated", emoji: "🚀", label: "Motivated" },
  { id: "energetic", emoji: "⚡", label: "Energetic" },
  { id: "relaxed", emoji: "😴", label: "Relaxed" },
  { id: "focused", emoji: "🎯", label: "Focused" },
  { id: "creative", emoji: "🎨", label: "Creative" },
  { id: "adventurous", emoji: "🗺️", label: "Adventurous" },
  { id: "curious", emoji: "🤔", label: "Curious" },
  { id: "surprised", emoji: "😮", label: "Surprised" },
  { id: "nostalgic", emoji: "📷", label: "Nostalgic" },
  { id: "romantic", emoji: "💖", label: "Romantic" },
];

const activities = [
  { id: "traveling", emoji: "✈️", label: "Traveling" },
  { id: "working", emoji: "💼", label: "Working" },
  { id: "studying", emoji: "📚", label: "Studying" },
  { id: "exercising", emoji: "🏃‍♂️", label: "Exercising" },
  { id: "cooking", emoji: "👨‍🍳", label: "Cooking" },
  { id: "reading", emoji: "📖", label: "Reading" },
  { id: "watching", emoji: "📺", label: "Watching" },
  { id: "listening", emoji: "🎧", label: "Listening" },
  { id: "playing", emoji: "🎮", label: "Playing" },
  { id: "shopping", emoji: "🛒", label: "Shopping" },
  { id: "cleaning", emoji: "🧹", label: "Cleaning" },
  { id: "gardening", emoji: "🌱", label: "Gardening" },
  { id: "painting", emoji: "🎨", label: "Painting" },
  { id: "writing", emoji: "✍️", label: "Writing" },
  { id: "dancing", emoji: "💃", label: "Dancing" },
  { id: "singing", emoji: "🎤", label: "Singing" },
  { id: "photography", emoji: "📸", label: "Photography" },
  { id: "gaming", emoji: "🎮", label: "Gaming" },
  { id: "meditation", emoji: "🧘‍♂️", label: "Meditation" },
  { id: "yoga", emoji: "🧘‍♀️", label: "Yoga" },
  { id: "running", emoji: "🏃‍♀️", label: "Running" },
  { id: "cycling", emoji: "🚴‍♂️", label: "Cycling" },
  { id: "swimming", emoji: "🏊‍♂️", label: "Swimming" },
  { id: "hiking", emoji: "🏔️", label: "Hiking" },
  { id: "camping", emoji: "⛺", label: "Camping" },
  { id: "fishing", emoji: "🎣", label: "Fishing" },
  { id: "golfing", emoji: "⛳", label: "Golfing" },
  { id: "tennis", emoji: "🎾", label: "Tennis" },
  { id: "basketball", emoji: "🏀", label: "Basketball" },
  { id: "football", emoji: "⚽", label: "Football" },
  { id: "volleyball", emoji: "🏐", label: "Volleyball" },
  { id: "baseball", emoji: "⚾", label: "Baseball" },
  { id: "soccer", emoji: "⚽", label: "Soccer" },
  { id: "hockey", emoji: "🏒", label: "Hockey" },
  { id: "boxing", emoji: "🥊", label: "Boxing" },
  { id: "martial_arts", emoji: "🥋", label: "Martial Arts" },
  { id: "weightlifting", emoji: "🏋️‍♂️", label: "Weightlifting" },
  { id: "pilates", emoji: "🧘‍♀️", label: "Pilates" },
  { id: "zumba", emoji: "💃", label: "Zumba" },
  { id: "crossfit", emoji: "💪", label: "CrossFit" },
  { id: "rock_climbing", emoji: "🧗‍♂️", label: "Rock Climbing" },
  { id: "skateboarding", emoji: "🛹", label: "Skateboarding" },
  { id: "surfing", emoji: "🏄‍♂️", label: "Surfing" },
  { id: "skiing", emoji: "⛷️", label: "Skiing" },
  { id: "snowboarding", emoji: "🏂", label: "Snowboarding" },
  { id: "ice_skating", emoji: "⛸️", label: "Ice Skating" },
  { id: "rollerblading", emoji: "🛼", label: "Rollerblading" },
  { id: "parkour", emoji: "🤸‍♂️", label: "Parkour" },
  { id: "gymnastics", emoji: "🤸‍♀️", label: "Gymnastics" },
  { id: "cheerleading", emoji: "📣", label: "Cheerleading" },
  { id: "marching_band", emoji: "🎺", label: "Marching Band" },
  { id: "orchestra", emoji: "🎻", label: "Orchestra" },
  { id: "choir", emoji: "🎤", label: "Choir" },
  { id: "karaoke", emoji: "🎤", label: "Karaoke" },
];

export default function FeelingActivitySelector({ 
  selectedFeeling, 
  selectedActivity, 
  onFeelingSelect, 
  onActivitySelect, 
  onClose,
  onSubmit,
  submitLabel = "Send",
}) {
  const [activeTab, setActiveTab] = useState("feeling");

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const handleFeelingTap = (feelingId) => {
    onActivitySelect(null);
    onFeelingSelect(selectedFeeling === feelingId ? null : feelingId);
  };

  const handleActivityTap = (activityId) => {
    onFeelingSelect(null);
    onActivitySelect(selectedActivity === activityId ? null : activityId);
  };

  const modal = (
    <>
      <div
        className="feeling_activity_overlay"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="feeling_activity_selector" role="dialog" aria-modal="true">
      <div className="selector_header">
        <div className="selector_tabs">
          <button 
            type="button"
            className={`tab ${activeTab === "feeling" ? "active" : ""}`}
            onClick={() => setActiveTab("feeling")}
            aria-pressed={activeTab === "feeling"}
          >
            Feeling
          </button>
          <button 
            type="button"
            className={`tab ${activeTab === "activity" ? "active" : ""}`}
            onClick={() => setActiveTab("activity")}
            aria-pressed={activeTab === "activity"}
          >
            Activity
          </button>
        </div>
        <button 
          type="button"
          className="close_btn" 
          onClick={onClose}
          aria-label="Close feeling activity selector"
        >
          <i className="exit_icon"></i>
        </button>
      </div>

      <div className="selector_content">
        {activeTab === "feeling" ? (
          <div className="feeling_grid">
            {feelings.map((feeling) => (
              <SelectorGridButton
                key={feeling.id}
                className={`feeling_item ${selectedFeeling === feeling.id ? "selected" : ""}`}
                selected={selectedFeeling === feeling.id}
                label={feeling.label}
                emoji={feeling.emoji}
                emojiClassName="feeling_emoji"
                labelClassName="feeling_label"
                onSelect={() => handleFeelingTap(feeling.id)}
              />
            ))}
          </div>
        ) : (
          <div className="activity_grid">
            {activities.map((activity) => (
              <SelectorGridButton
                key={activity.id}
                className={`activity_item ${selectedActivity === activity.id ? "selected" : ""}`}
                selected={selectedActivity === activity.id}
                label={activity.label}
                emoji={activity.emoji}
                emojiClassName="activity_emoji"
                labelClassName="activity_label"
                onSelect={() => handleActivityTap(activity.id)}
              />
            ))}
          </div>
        )}
      </div>

  {onSubmit && (
        <div className="selected_items">
          {selectedFeeling && (
            <div className="selected_item">
              <span className="selected_emoji notranslate" translate="no">
                {feelings.find(f => f.id === selectedFeeling)?.emoji}
              </span>
              <span className="selected_text">
                {feelings.find(f => f.id === selectedFeeling)?.label}
              </span>
              <button 
                className="remove_btn"
                onClick={() => onFeelingSelect(null)}
                title="Cancel "
                aria-label="Cancel "
              >
                <FaTimes />
              </button>
            </div>
          )}
          {selectedActivity && (
            <div className="selected_item">
              <span className="selected_emoji notranslate" translate="no">
                {activities.find(a => a.id === selectedActivity)?.emoji}
              </span>
              <span className="selected_text">
                {activities.find(a => a.id === selectedActivity)?.label}
              </span>
              <button 
                className="remove_btn"
                onClick={() => onActivitySelect(null)}
                title="Cancel "
                aria-label="Cancel "
              >
                <FaTimes />
              </button>
            </div>
          )}
          <button
            className="send_fab"
            onClick={() => onSubmit()}
            disabled={!selectedFeeling && !selectedActivity}
            title={submitLabel}
            aria-label={submitLabel}
            type="button"
          >
            <FaPaperPlane className="send_icon" />
          </button>
        </div>
      )}
      </div>
    </>
  );

  return createPortal(modal, document.body);
}